/**
 * Sidebar nav. Click handler dispatches view changes via callback.
 * Updates count-badges from `counts` events and storage-list from `storage` events.
 */
import { icon } from '../core/icons.js';
import { mountBusyIndicator } from '../core/busyIndicator.js';
import { gate } from '../core/capabilities.js';

export class Sidebar {
  constructor({ onView }) {
    this.onView = onView;
    this._dedupNewVal = 0;
    this.el = document.getElementById('sidebar');
    if (!this.el) return;

    // Inject icons into nav items
    for (const item of this.el.querySelectorAll('.nav__item')) {
      const ic = item.dataset.icon;
      const slot = item.querySelector('.nav__ic');
      if (ic && slot) slot.innerHTML = icon(ic, { size: 16 });
    }
    // Brand icon
    const brand = this.el.querySelector('.brand__mark');
    if (brand) brand.innerHTML = icon('bolt', { size: 18 });

    // Global busy-indicator: zeigt im Brand-Bereich einen Spinner sobald
    // zapd im Hintergrund arbeitet (active session, keyframe-scan, …).
    const brandRow = this.el.querySelector('.sidebar__brand') ||
                     this.el.querySelector('.brand') ||
                     brand?.parentElement;
    if (brandRow) {
      const slot = document.createElement('div');
      slot.className = 'sidebar__busy-slot';
      brandRow.appendChild(slot);
      try { mountBusyIndicator(slot); } catch (e) {
        console.warn('busy-indicator mount failed', e);
      }
    }

    // Click handler
    this.el.addEventListener('click', e => {
      const item = e.target.closest('.nav__item');
      if (item && item.dataset.view) {
        this._activate(item.dataset.view);
        this.onView?.(item.dataset.view);
      }
      const stItem = e.target.closest('.storage__item');
      if (stItem) {
        // clicking a storage opens files-view
        this._activate('files');
        this.onView?.('files');
      }
    });
  }

  setView(view) {
    if (!this.el) return;
    for (const item of this.el.querySelectorAll('.nav__item')) {
      item.classList.toggle('is-active', item.dataset.view === view);
    }
  }

  /**
   * Show admin-only sidebar entries (data-admin-only attribute) when role==='admin'.
   * Also applies to admin-only chrome elements throughout the page (KPI strip,
   * statusbar, magnet button, action sections) — anything in the document with
   * data-admin-only gets hidden for non-admins.
   */
  applyRole(role) {
    this._role = role;
    const isAdmin = role === 'admin';
    for (const el of document.querySelectorAll('[data-admin-only]')) {
      el.hidden = !isAdmin;
    }
  }

  /**
   * Cap-driven gating (B3/B7): show/hide every [data-cap] element by the caller's
   * effective caps. Until the manifest is live, gate() falls back per element to
   * `data-cap-default` — "all" = visible to everyone (consumer modules
   * video/music/photos), otherwise admin-only (the pre-migration data-admin-only
   * origin: adult/private/torrent/discover/rss). The remaining legacy
   * [data-admin-only] entries (admin infrastructure) gate on user.manage, role as
   * the not-live fallback. Runs right after applyRole — and again is safe to
   * re-run — so a not-live manifest never opens a sensitive module (no fail-open).
   */
  applyCapabilities() {
    const isAdmin = this._role === 'admin';
    for (const el of document.querySelectorAll('[data-cap]')) {
      const fallback = el.dataset.capDefault === 'all' ? true : isAdmin;
      el.hidden = !gate(el.dataset.cap, fallback);
    }
    for (const el of document.querySelectorAll('[data-admin-only]')) {
      el.hidden = !gate('user.manage', isAdmin);
    }
  }

  _activate(view) { this.setView(view); }

  applyCounts(counts) {
    if (!this.el) return;
    for (const [key, val] of Object.entries(counts)) {
      const badge = this.el.querySelector(`[data-cnt="${key}"]`);
      if (badge) badge.textContent = val;
    }
    // dedup_new kommt über applyCounts → internen State synchronisieren,
    // damit bumpDedupNew(±1) auf der DB-Wahrheit aufbaut.
    if ('dedup_new' in counts) {
      this._dedupNewVal = Math.max(0, parseInt(counts.dedup_new, 10) || 0);
      const badge = this.el.querySelector('[data-cnt="dedup-new"]');
      if (badge) badge.textContent = this._dedupNewVal;
    }
  }

  /** Hard-set der dedup-new-Badge (vom 6s-Reconcile). */
  setDedupNew(n) {
    this._dedupNewVal = Math.max(0, parseInt(n, 10) || 0);
    const badge = this.el?.querySelector('[data-cnt="dedup-new"]');
    if (badge) badge.textContent = this._dedupNewVal;
  }

  /** Delta-Update der dedup-new-Badge (vom SSE-Live-Event oder lokaler Action). */
  bumpDedupNew(delta) {
    this.setDedupNew(this._dedupNewVal + (parseInt(delta, 10) || 0));
  }

  /**
   * Setzt die ops-live-Badge auf eine Rate (Events/Minute).
   * 0 → grauer Punkt "●", sonst Zahl.
   */
  setOpsRate(rate) {
    const badge = this.el?.querySelector('[data-cnt="ops-live"]');
    if (!badge) return;
    const n = parseInt(rate, 10) || 0;
    badge.textContent = n > 0 ? String(n) : '●';
  }

  /**
   * Setzt die hls-live-Badge auf die Anzahl aktiver HLS-Streams.
   * 0 → grauer Punkt "●", sonst Zahl.
   */
  setHlsLive(count) {
    const badge = this.el?.querySelector('[data-cnt="hls-live"]');
    if (!badge) return;
    const n = parseInt(count, 10) || 0;
    badge.textContent = n > 0 ? String(n) : '●';
  }

  applyStorage(storages) {
    const wrap = document.getElementById('storage-list');
    if (!wrap) return;
    wrap.innerHTML = storages.map(s => `
      <button class="storage__item ${s.ok ? 'is-online' : 'is-offline'}" data-storage="${s.name}" type="button">
        <span class="storage__dot"></span>
        <span class="storage__name">${s.name}</span>
        <span class="storage__state">${s.ok ? 'online' : 'offline'}</span>
      </button>
    `).join('');
  }
}
